import React, { useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Icons,
  Modal,
  Text,
  TextField,
  useTheme,
} from '@ublend-npm/aulaui-core';

interface EditGroupNameModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (groupName: string) => void;
  initialValue?: string;
  isLoading?: boolean;
  handleChangeName?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const EditGroupNameModal = ({
  open,
  onClose,
  onSave,
  initialValue = '',
  isLoading = false,
  handleChangeName,
}: EditGroupNameModalProps) => {
  const [groupName, setGroupName] = useState(initialValue);
  const { palette } = useTheme();

  // Update local state when initialValue changes or when modal opens
  React.useEffect(() => {
    if (open) {
      setGroupName(initialValue);
    }
  }, [initialValue, open]);

  // Focus management for voice over
  const titleRef = React.useRef<HTMLElement>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setGroupName(newValue);
    // Call the parent's handler if provided
    if (handleChangeName) {
      handleChangeName(e);
    }
  };

  const handleSave = () => {
    if (groupName.trim()) {
      onSave(groupName.trim());
    }
  };

  const handleClose = () => {
    setGroupName(initialValue);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="edit-group-name-title"
      aria-describedby="edit-group-name-description"
    >
      <Box
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-group-name-title"
        sx={{
          position: 'absolute',
          top: {
            xs: '40%',
            sm: '50%',
          },
          left: {
            xs: '32px',
            sm: '50%',
          },
          right: {
            xs: '32px',
            sm: 'auto',
          },
          transform: {
            xs: 'none',
            sm: 'translate(-50%, -50%)',
          },
          width: {
            xs: 'auto',
            sm: '560px',
          },
          height: '202px',
          // maxWidth: '90vw',
          bgcolor: palette.background.paper,
          borderRadius: '8px',
          boxShadow: 24,
          padding: {
            xs: '24px 16px',
            sm: '24px 16px',
          },
          outline: 'none',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            // p: '24px 16px',
            // borderBottom: `1px solid ${palette.divider}`,
          }}
        >
          <Text
            id="edit-group-name-title"
            component="h2"
            ref={titleRef}
            tabIndex={-1}
            sx={{
              fontWeight: 600,
              fontSize: '1.25rem',
              color: palette.text.primary,
              outline: 'none', // Remove focus outline since this is for screen readers
            }}
          >
            Edit group name
          </Text>
          <IconButton
            onClick={handleClose}
            size="small"
            aria-label="Close edit group name dialog"
            // kind="secondary"
            sx={{
              minWidth: 'auto',
              width: '24px',
              height: '24px',
              // borderRadius: '50%',
              p: '5px',
            }}
          >
            <Icons.Close />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ mt: '8px' }}>
          <TextField
            label="Group name"
            placeholder="Enter group name"
            value={groupName}
            onChange={handleInputChange}
            aria-describedby="edit-group-name-description"
            aria-required="true"
            aria-invalid={!groupName.trim() ? 'true' : 'false'}
          />
          <Box>
            <Button
              fullWidth
              onClick={handleSave}
              disabled={!groupName.trim() || isLoading}
              kind="primary"
              aria-label={
                isLoading
                  ? 'Saving group name changes'
                  : 'Save group name changes'
              }
              sx={{
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: palette.primary.dark,
                },
              }}
              loading={isLoading}
            >
              {isLoading ? 'Saving...' : 'Save changes'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default EditGroupNameModal;
